import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

// M5.2: маршруты. Один SPA обслуживает две роли:
//   /admin/*    — admin-кабинет (X-Admin-Token / cookie session).
//   /cabinet/*  — кабинет покупателя (magic-link / cookie session).
//
// Роли защищены `meta.requireRole`. Router guard ниже сверяет с
// auth.user.role и редиректит на нужный login.

const routes: RouteRecordRaw[] = [
  // ---- Admin ----
  {
    path: '/admin/login',
    name: 'admin-login',
    component: () => import('@/views/admin/LoginView.vue'),
    meta: { layout: 'blank' },
  },
  {
    path: '/admin',
    component: () => import('@/components/AppLayout.vue'),
    meta: { requireRole: 'admin' },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard',
      },
      {
        path: 'dashboard',
        name: 'admin-dashboard',
        component: () => import('@/views/admin/DashboardView.vue'),
      },
      {
        path: 'licenses',
        name: 'admin-licenses',
        component: () => import('@/views/admin/LicensesView.vue'),
      },
      {
        path: 'license/:id',
        name: 'admin-license-detail',
        component: () => import('@/views/admin/LicenseDetailView.vue'),
        props: true,
      },
    ],
  },

  // ---- Customer cabinet (M5.4) ----
  {
    path: '/cabinet/login',
    name: 'customer-login',
    component: () => import('@/views/customer/LoginView.vue'),
    meta: { layout: 'blank' },
  },
  {
    path: '/cabinet',
    component: () => import('@/components/AppLayout.vue'),
    meta: { requireRole: 'customer' },
    children: [
      {
        path: '',
        redirect: '/cabinet/dashboard',
      },
      {
        path: 'dashboard',
        name: 'customer-dashboard',
        component: () => import('@/views/customer/DashboardView.vue'),
      },
      {
        // M5.4.2: карточка одной лицензии покупателя.
        path: 'license/:id',
        name: 'customer-license-detail',
        component: () => import('@/views/customer/LicenseDetailView.vue'),
        props: true,
      },
    ],
  },

  // ---- Root + 404 ----
  {
    path: '/',
    redirect: () => {
      // Если пользователь уже залогинен — редирект на соответствующий
      // дашборд. Иначе — на admin/login по умолчанию (кабинет
      // покупателя обычно открывается из письма с magic-link'ом).
      const auth = useAuthStore();
      if (auth.user?.role === 'admin') return '/admin';
      if (auth.user?.role === 'customer') return '/cabinet';
      return '/admin/login';
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { layout: 'blank' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_, __, savedPosition) {
    return savedPosition ?? { top: 0 };
  },
});

// Router guard: проверяем требуемую роль ДО рендера компонента.
// auth.bootstrap() уже вызван в App.vue mounted, к моменту первой
// навигации стор инициализирован.
router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Дать первой загрузке завершить bootstrap (читает /api/session/me).
  if (!auth.bootstrapped) {
    await auth.bootstrap();
  }

  const requireRole = to.meta.requireRole as 'admin' | 'customer' | undefined;
  if (!requireRole) return true; // public route

  if (!auth.user) {
    // Не авторизован → на соответствующий login.
    const loginRoute = requireRole === 'admin' ? '/admin/login' : '/cabinet/login';
    return { path: loginRoute, query: { next: to.fullPath } };
  }

  if (auth.user.role !== requireRole) {
    // Авторизован, но другой ролью → редирект на свой дашборд.
    return auth.user.role === 'admin' ? '/admin' : '/cabinet';
  }

  return true;
});

export default router;
